/** The one inline work area of the Operations tab: opens the canonical form for the step in hand, full width, above the wide grid. */
import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { ShipmentForm } from "./InlineForms";
import { DcWorkspace } from "./DcWorkspace";
import { PaymentForm, TermsForm, FeedbackForm, SupportForm } from "./InlineFinance";

const SHIPPED = ["PICKED_UP", "DISPATCHED", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED"];
const TITLE = { DC: "Fulfilment workspace · recipient · devices · values · courier · final DC", SHIPMENT: "Courier · AWB · dispatch · delivery", PAYMENT: "Record payment received", TERMS: "Invoice due date / payment terms", FEEDBACK: "Delivery feedback", SUPPORT: "Resolve support issue", DELIVERY: "Units not yet delivered" };

// keeps the form heading below the sticky quote header / tabs of the container that scrolls
const scroller = (el) => { let p = el.parentElement; while (p && !/(auto|scroll)/.test(getComputedStyle(p).overflowY)) p = p.parentElement; return p; };
function stickyOffset(el) {
  const p = scroller(el);
  const hs = [...document.querySelectorAll(".sticky, header")].filter((s) => !s.contains(el) && !el.contains(s) && getComputedStyle(s).position === "sticky" && scroller(s) === p)
    .map((s) => (parseFloat(getComputedStyle(s).top) || 0) + s.getBoundingClientRect().height).filter((h) => h < 300);
  return Math.max(16, ...hs) + 24;
}

function DeliveryList({ rows, open }) {
  const pending = rows.filter((r) => r.delivery_status !== "DELIVERED");
  if (!pending.length) return <div className="text-[12px] text-emerald-800" data-testid="inline-delivery-none">Every recipient is delivered.</div>;
  return (
    <ul className="divide-y divide-slate-100 rounded-md bg-white" data-testid="inline-delivery-list">
      {pending.map((r) => (
        <li key={r.allocation_id} className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-[11.5px]" data-testid={`inline-delivery-${r.allocation_id}`}>
          <b className="min-w-[140px]">{r.recipient_name}</b>
          <span className="text-slate-600">{SHIPPED.includes(r.state) ? String(r.state).replace(/_/g, " ").toLowerCase() : r.blocker || "not dispatched"}</span>
          {r.dc_draft && r.dc_id && <button onClick={() => open("DC", r)} className="ml-auto rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`inline-delivery-dc-${r.allocation_id}`}>GENERATE FINAL DC</button>}
          {r.shipment_id && !r.dc_draft && <button onClick={() => open("SHIPMENT", r)} className="ml-auto rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`inline-delivery-ship-${r.allocation_id}`}>{SHIPPED.includes(r.state) ? "UPDATE DELIVERY" : "COURIER / AWB"}</button>}
        </li>))}
    </ul>
  );
}

export function InlineWork({ qid, st, panel, setPanel, reload }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!panel || !el) return;
    const p = scroller(el);
    const off = Math.max(128, stickyOffset(el));
    if (p) p.scrollTo({ top: p.scrollTop + el.getBoundingClientRect().top - p.getBoundingClientRect().top - off, behavior: "smooth" });
    else window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().top - off, behavior: "smooth" });
  }, [panel]);
  if (!panel) return null;
  const { kind, row, invoiceId } = panel;
  const close = () => setPanel(null);
  const done = () => { close(); reload(); };
  const open = (k, r) => setPanel({ kind: k, row: r });
  const body = kind === "DC" ? <DcWorkspace key={row.dc_id} qid={qid} r={row} done={done} defCourier={st.defaults?.courier} />
    : kind === "SHIPMENT" ? <ShipmentForm key={row.shipment_id} qid={qid} r={row} done={done} defCourier={st.defaults?.courier} shipped={SHIPPED.includes(row.state)} />
    : kind === "PAYMENT" ? <PaymentForm qid={qid} invoices={st.invoices} invoiceId={invoiceId} done={done} />
    : kind === "TERMS" ? <TermsForm qid={qid} invoices={st.invoices} invoiceId={invoiceId} done={done} />
    : kind === "FEEDBACK" ? <FeedbackForm orderId={st.order.id} r={row} auto={st.feedback_automation} done={done} />
    : kind === "SUPPORT" ? <SupportForm qid={qid} rows={st.rows} done={done} />
    : kind === "DELIVERY" ? <DeliveryList rows={st.rows} open={open} /> : null;
  return (
    <section ref={ref} className="scroll-mt-32 rounded-xl border-2 border-sky-700 bg-sky-50/60 p-3" data-testid="ops-inline-work" data-kind={kind}>
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-sky-900" data-testid="ops-inline-title">{TITLE[kind]}</h4>
        <span className="text-[10.5px] text-slate-500">on this quotation · {st.quote?.code}</span>
        <button onClick={close} className="ml-auto rounded-md p-1 text-slate-500 hover:bg-white" aria-label="Close" data-testid="ops-inline-close"><X className="h-4 w-4" /></button>
      </div>
      {body}
    </section>
  );
}
